import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import EditMarkdownTrigger from "@ext/article/actions/EditMarkdownTrigger";
import t from "@ext/localization/locale/translate";
import { ConfirmMenuItem } from "@ui-kit/ConfirmPopover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@ui-kit/Dropdown";
import { FloatingTriggerButton } from "@ui-kit/FloatingPanel";
import { Icon } from "@ui-kit/Icon";
import { useCallback } from "react";

type AgentSkillActionsProps = {
	id: string;
	onDelete: (id: string) => void;
	onMarkdownChange: (id: string, markdown: string) => void;
	className?: string;
};

export const AgentSkillActions = ({ id, onDelete, onMarkdownChange, className }: AgentSkillActionsProps) => {
	const apiUrlCreator = ApiUrlCreatorService.value;

	const loadContent = useCallback(async () => {
		const response = await FetchService.fetch(apiUrlCreator.getFileContentInGramaxDir(id, "agentSkill"));
		return response.ok ? response.text() : "";
	}, [id]);

	const saveContent = useCallback(
		async (content: string) => {
			const response = await FetchService.fetch(
				apiUrlCreator.updateFileInGramaxDir(id, "agentSkill"),
				JSON.stringify({ content }),
			);
			if (response.ok) onMarkdownChange(id, content);
		},
		[id, onMarkdownChange],
	);

	return (
		<DropdownMenu modal={false}>
			<DropdownMenuTrigger asChild>
				<FloatingTriggerButton
					aria-label={t("actions")}
					className={className}
					onClick={(event) => event.stopPropagation()}
				>
					<Icon icon="ellipsis" size="sm" />
				</FloatingTriggerButton>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				<EditMarkdownTrigger
					isCurrentItem
					isTemplate={false}
					loadContent={loadContent}
					saveContent={saveContent}
				/>
				<DeleteSkillItem id={id} onDelete={onDelete} />
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

const DeleteSkillItem = ({ id, onDelete }: Pick<AgentSkillActionsProps, "id" | "onDelete">) => {
	const apiUrlCreator = ApiUrlCreatorService.value;

	const deleteSkill = async () => {
		const response = await FetchService.fetch(apiUrlCreator.removeFileInGramaxDir(id, "agentSkill"));
		if (response.ok) onDelete(id);
	};

	return (
		<ConfirmMenuItem
			confirm={{ title: t("confirm-agent-skills-delete"), confirmText: t("delete"), icon: "trash-2" }}
			onConfirm={deleteSkill}
			type="danger"
		>
			<Icon icon="trash-2" />
			{t("delete")}
		</ConfirmMenuItem>
	);
};
